/**
 * Typed API errors.
 *
 * `catchAxiosError` used to flatten every failure into a bare `Error` (or, in
 * one branch, a raw string), which threw away the one thing callers needed: why
 * the request failed. A dead session, a network that dropped mid-request, and a
 * user who genuinely has no profile all arrived looking identical, so the first
 * component to see an error had to guess — and guessed "no account".
 */

export const API_ERROR_KINDS = {
  // The request never got an answer. Says nothing about the session.
  NETWORK: "network",
  // 401 — the access token is dead. Re-authentication is the only way forward.
  SESSION_EXPIRED: "session-expired",
  // 404 — the server answered, and the answer is that the record isn't there.
  NOT_FOUND: "not-found",
};

export class ApiError extends Error {
  constructor(message, { kind, status = null, code = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.code = code;
  }
}

// Duck-typed rather than `instanceof`: errors cross a structuredClone through
// the query broadcast channel, which strips the prototype.
const hasKind = (error, kind) =>
  !!error && typeof error === "object" && error.kind === kind;

/**
 * The request never reached the server, or its answer never made it back.
 *
 * Waking a laptop is the common case: Chrome cancels every in-flight request as
 * `ERR_NETWORK_CHANGED` when the interface comes back. Nothing is known about
 * the session, so the only correct response is to try again.
 */
export const isNetworkError = (error) =>
  hasKind(error, API_ERROR_KINDS.NETWORK);

/** The server rejected the access token. */
export const isSessionExpiredError = (error) =>
  hasKind(error, API_ERROR_KINDS.SESSION_EXPIRED);

/** The server answered, and the record does not exist. */
export const isNotFoundError = (error) =>
  hasKind(error, API_ERROR_KINDS.NOT_FOUND);
